<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { useMasifiWhatsappQrStatus } from 'dashboard/composables/masifi/useMasifiWhatsappQrStatus';
import Banner from 'dashboard/components/ui/Banner.vue';
import GroupsAPI from 'dashboard/api/masifi/masifiWhatsappQrGroups';
import {
  isWhatsappQrInbox,
  WHATSAPP_QR_FEATURE,
} from 'dashboard/helper/masifi/masifiWhatsappQr';
import {
  isMasifiWaGroupChat,
  masifiWaLastActivityId,
} from 'dashboard/helper/masifi/masifiWaGroups';

// Aviso a los agentes cuando el número de WhatsApp QR del inbox no está conectado (spec §3.2, F2 ruling 15).
// Solo el estado grueso: el detalle técnico sigue en el panel admin del inbox. Sin nada para `ok`/`null`.
const props = defineProps({ inbox: { type: Object, default: null } });

const { t } = useI18n();
const { isAdmin } = useAdmin();
const { state } = useMasifiWhatsappQrStatus(() => props.inbox);

// estado → [clave i18n, color-scheme del Banner nativo, ¿tiene variante admin?]
const BANNERS = {
  reconnecting: ['RECONNECTING', 'warning', false],
  paused: ['PAUSED', 'warning', true],
  disconnected: ['DISCONNECTED', 'alert', true],
  disabled: ['DISABLED', 'alert', false],
};

const connectionBanner = computed(() => {
  const entry = BANNERS[state.value];
  if (!entry) return null;
  const [key, colorScheme, hasAdminVariant] = entry;
  const suffix = hasAdminVariant && isAdmin.value ? '_ADMIN' : '';
  return {
    message: t(`MASIFI_WHATSAPP_QR.AGENT_BANNER.${key}${suffix}`),
    colorScheme,
  };
});

// F3a (spec §6.1, ruling 18): en un grupo, además del estado del número, por qué este número no puede escribir ahí. El
// servidor bloquea igual (failed con motivo); el banner lo avisa antes. Se relee con cada línea de actividad nueva.
const currentChat = useMapGetter('getSelectedChat');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const groupState = ref(null);
// Final review A-m1: with the module off the group endpoint is a guaranteed 403 → do not ask.
const isGroupChat = computed(
  () =>
    isWhatsappQrInbox(props.inbox) &&
    isFeatureEnabled.value(accountId.value, WHATSAPP_QR_FEATURE) &&
    isMasifiWaGroupChat(currentChat.value)
);
const fetchGroup = async () => {
  groupState.value = null;
  if (!isGroupChat.value || !currentChat.value?.id) return;
  try {
    groupState.value = (await GroupsAPI.get(currentChat.value.id)).data;
  } catch (error) {
    groupState.value = null;
  }
};
watch(
  () => [
    currentChat.value?.id,
    isGroupChat.value,
    masifiWaLastActivityId(currentChat.value?.messages),
  ],
  fetchGroup,
  { immediate: true }
);

const groupBanner = computed(() => {
  const ours = groupState.value?.our_number;
  if (!ours) return null;
  if (!ours.groups_enabled)
    return {
      message: t(
        `MASIFI_WHATSAPP_QR.GROUPS.BANNER.DISABLED${isAdmin.value ? '_ADMIN' : ''}`
      ),
      colorScheme: 'alert',
    };
  if (ours.left)
    return {
      message: t('MASIFI_WHATSAPP_QR.GROUPS.BANNER.LEFT'),
      colorScheme: 'alert',
    };
  // Reconciliación F3b: tras cambiar el número que atiende, este hilo (el viejo) ya no recibe los mensajes del grupo.
  if (groupState.value.handling_inbox_id && ours.handling === false) {
    return {
      message: t('MASIFI_WHATSAPP_QR.GROUPS.BANNER.NOT_HANDLING'),
      colorScheme: 'warning',
    };
  }
  if (
    groupState.value.announce &&
    ours.role &&
    !['admin', 'superadmin'].includes(ours.role)
  ) {
    return {
      message: t('MASIFI_WHATSAPP_QR.GROUPS.BANNER.ADMINS_ONLY'),
      colorScheme: 'warning',
    };
  }
  return null;
});

const banner = computed(() => connectionBanner.value || groupBanner.value);
</script>

<template>
  <Banner
    v-if="banner"
    :color-scheme="banner.colorScheme"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="banner.message"
  />
</template>
